import React, { useState } from 'react';
import {
  CoverSlide,
  ContentSlide,
  TwoColumnSlide,
  TableSlide,
  RoadmapSlide,
  MilestoneSlide,
  ActionableSlide,
} from '../../../commercial-proposal';
import '../../../commercial-proposal/styles/Presentation.css';
import architectureSvg from './assets/propuesta_arquitectura.png';
import lookAndFeelImg from './assets/look_and_feel.png';

const Presentation: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  const handleMouseLeave = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    setShowControls(false);
  };

  // Footer común para todas las slides
  const commonFooter = {
    estimationCode: 'Arquitectura / SM085-08',
    versionText: 'Versión 1.0 — Enero 21, 2025',
    confidentialText: 'Confidencial',
    websiteUrl: 'www.simphony.com.co',
  };

  const slides = [
    // Slide 1: Portada
    <CoverSlide
      key="cover"
      title="Deprisa - Rearquitectura + APIM"
      footer={commonFooter}
    />,

    // Slide 2: Contexto y problemática actual
    <ContentSlide
      key="context"
      title="Contexto y desafíos actuales"
      content={
        <div>
          <p>
            La arquitectura actual de Deprisa presenta desafíos en términos de escalabilidad,
            integración entre sistemas y gestión centralizada de APIs. La implementación de un
            API Management (APIM) permitirá unificar el acceso, controlar el tráfico y
            monitorear el uso de servicios.
          </p>
          <p>
            Esta <strong>rearquitectura</strong> establece las bases para un ecosistema digital
            más robusto, facilitando la integración con partners, mejorando la seguridad y
            permitiendo una mejor observabilidad de los sistemas.
          </p>
        </div>
      }
      footer={commonFooter}
    />,

    // Slide 3: Objetivos del proyecto
    <TwoColumnSlide
      key="objectives"
      title="Objetivos del Proyecto"
      leftColumn={{
        title: 'Seguridad y Control',
        subtitle: 'Reducción de riesgos y gobernanza',
        backgroundColor: '#f5f5f5',
        items: [
          { number: '01', text: 'Eliminar la exposición insegura actual (trackingId no debe actuar como credencial)' },
          { number: '02', text: 'Centralizar el gobierno de APIs en Azure API Management' },
          { number: '03', text: 'Separar correctamente Front, API y CMS (desacople arquitectónico)' },
        ],
      }}
      rightColumn={{
        title: 'Escalabilidad y Negocio',
        subtitle: 'Plataforma sostenible a largo plazo',
        backgroundColor: '#ffffff',
        items: [
          { number: '04', text: 'Exponer información por niveles (scopes) según tipo de consumidor' },
          { number: '05', text: 'Soportar múltiples canales: Web, App, B2B, Call Center' },
          { number: '06', text: 'Establecer un modelo repetible, auditable y gobernado' },
        ],
      }}
      footer={commonFooter}
    />,

    // Slide 4: Arquitectura Objetivo
    <ContentSlide
      key="architecture-objective"
      title="Arquitectura Objetivo"
      content={
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem' }}>
          <img 
            src={architectureSvg} 
            alt="Diagrama de Arquitectura" 
            style={{ width: '100%', maxHeight: '500px', objectFit: 'contain' }} 
          />
          <div style={{ background: '#f5f5f5', padding: '1.5rem', borderRadius: '8px', width: '100%' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Flujo Simplificado</h4>
            <p style={{ margin: 0, fontSize: '1rem', color: '#555' }}>
              Internet → WAF → APIM → Firewall → BFF → Hub
            </p>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
              Esta arquitectura desacopla el frontend del backend, centraliza el gobierno en el APIM y asegura el tráfico en múltiples capas.
            </p>
          </div>
        </div>
      }
      footer={commonFooter}
    />,

    // Slide 5: Look and Feel UX inicial
    <ContentSlide
      key="ux-look-and-feel"
      title="Look & Feel UX Inicial"
      content={
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img
            src={lookAndFeelImg}
            alt="Look and Feel UX inicial"
            style={{ width: '100%', maxWidth: '1400px', objectFit: 'contain' }}
          />
        </div>
      }
      footer={commonFooter}
    />,

    // Slide 5: Modelo de Seguridad por Scopes
    <TableSlide
      key="security-model"
      title="Modelo de Seguridad por Scopes"
      subtitle="Cada nivel de acceso determina qué información puede ver el consumidor"
      headers={['Scope', 'Autenticación', 'Estado', 'Timeline', 'POD', 'Referencias']}
      rows={[
        {
          Scope: 'tracking.basic',
          Autenticación: 'Sin auth',
          Estado: '✓',
          Timeline: '—',
          POD: '—',
          Referencias: '—',
        },
        {
          Scope: 'tracking.events',
          Autenticación: 'OAuth2',
          Estado: '✓',
          Timeline: '✓',
          POD: '—',
          Referencias: '—',
        },
        {
          Scope: 'tracking.pod',
          Autenticación: 'OAuth2 + step-up',
          Estado: '✓',
          Timeline: '✓',
          POD: '✓',
          Referencias: '—',
        },
        {
          Scope: 'tracking.full',
          Autenticación: 'Client Credentials',
          Estado: '✓',
          Timeline: '✓',
          POD: '✓',
          Referencias: '✓',
        },
        {
          Scope: 'tracking.internal.*',
          Autenticación: 'Entra ID (SSO)',
          Estado: '✓',
          Timeline: '✓',
          POD: '✓',
          Referencias: '✓',
        },
      ]}
      footer={commonFooter}
    />,

    // Slide 5: Roadmap de implementación
    <RoadmapSlide
      key="roadmap"
      title="Roadmap de Implementación"
      subtitle="5 Milestones en 8 semanas — Cada milestone es un entregable desplegado a producción"
      totalWeeks={8}
      milestones={[
        { id: 'M1', title: 'APIM Gateway y Control', weeks: '1-2', versionLabel: 'v1 Live' },
        { id: 'M2', title: 'Arquitectura + Seguridad', weeks: '3-4', versionLabel: 'v2 Live' },
        { id: 'M3', title: 'Funcionalidades y B2B', weeks: '5-6', versionLabel: 'v3 Live' },
        { id: 'M4', title: 'Interno y Gobierno', weeks: '7-8', versionLabel: 'v4 Live' },
        { id: 'M5', title: 'Calidad y Go-Live Final', weeks: '8', versionLabel: 'v5 Live' },
      ]}
      footer={commonFooter}
    />,

    // Slide 6: M1 - APIM Gateway y Control
    <MilestoneSlide
      key="m1-apim"
      title="M1: APIM Gateway y Control"
      subtitle="Semanas 1-2 → v1 Live"
      price="$11.081.633 COP"
      priceSubtitle="2 semanas de ejecución"
      deliverables={[
        {
          title: 'tracking.basic en producción',
          description: 'Scope básico operativo sobre WebApps existentes con APIM como gateway.',
        },
        {
          title: 'Azure API Management',
          description: 'Configuración inicial del gateway central con políticas de ruteo.',
        },
        {
          title: 'Rate Limiting & Throttling',
          description: 'Políticas básicas de control de tráfico y protección contra abuso.',
        },
        {
          title: 'Logging Centralizado',
          description: 'Monitorización en Azure Monitor con trazabilidad de requests.',
        },
        {
          title: 'Developer Portal',
          description: 'Portal inicial con documentación de API para consumidores.',
        },
      ]}
      footer={commonFooter}
    />,

    // Slide 7: M2 - Arquitectura + Seguridad
    <MilestoneSlide
      key="m2-arquitectura"
      title="M2: Arquitectura + Seguridad"
      subtitle="Semanas 3-4 → v2 Live"
      price="$12.795.918 COP"
      priceSubtitle="2 semanas de ejecución"
      deliverables={[
        {
          title: 'Separación Front/API',
          description: 'WebApps independientes con desacople arquitectónico completo.',
        },
        {
          title: 'OAuth2 + Azure Entra ID',
          description: 'Implementación de autenticación segura con tokens y flujos estándar.',
        },
        {
          title: 'tracking.events operativo',
          description: 'Scope con timeline de eventos disponible para usuarios autenticados.',
        },
        {
          title: 'BFF Pattern',
          description: 'Backend for Frontend optimizado para cada canal de consumo.',
        },
        {
          title: 'Políticas de Seguridad',
          description: 'Validación de JWT, CORS, headers de seguridad en APIM.',
        },
        {
          title: 'Modelo Zero Trust',
          description: 'Verificación continua de identidad y contexto en cada request.',
        },
      ]}
      footer={commonFooter}
    />,

    // Slide 8: M3 - Funcionalidades y B2B
    <MilestoneSlide
      key="m3-funcionalidades"
      title="M3: Funcionalidades y B2B"
      subtitle="Semanas 5-6 → v3 Live"
      price="$11.724.490 COP"
      priceSubtitle="2 semanas de ejecución"
      deliverables={[
        {
          title: 'tracking.pod operativo',
          description: 'Scope con acceso a prueba de entrega (POD) y firma digital.',
        },
        {
          title: 'Step-up Authentication',
          description: 'Verificación adicional para acceso a datos sensibles.',
        },
        {
          title: 'Integración B2B',
          description: 'Client Credentials para partners con tracking.full scope.',
        },
        {
          title: 'Rate Limiting Avanzado',
          description: 'Políticas diferenciadas por tipo de consumidor y plan.',
        },
        {
          title: 'Webhooks y Notificaciones',
          description: 'Sistema de callbacks para eventos de tracking en tiempo real.',
        },
        {
          title: 'SDK y Documentación B2B',
          description: 'Recursos para integración de partners externos.',
        },
      ]}
      footer={commonFooter}
    />,

    // Slide 9: M4 - Interno y Gobierno
    <MilestoneSlide
      key="m4-interno"
      title="M4: Interno y Gobierno"
      subtitle="Semanas 7-8 → v4 Live"
      price="$5.653.061 COP"
      priceSubtitle="2 semanas de ejecución"
      deliverables={[
        {
          title: 'tracking.internal.* operativo',
          description: 'Scopes internos para Call Center y operaciones.',
        },
        {
          title: 'Azure Entra ID SSO',
          description: 'Single Sign-On para empleados de Deprisa.',
        },
        {
          title: 'Portal de Gobierno',
          description: 'Dashboard de gestión y monitoreo de APIs.',
        },
        {
          title: 'Auditoría y Compliance',
          description: 'Logs de acceso y reportes de cumplimiento.',
        },
        {
          title: 'Roles y Permisos',
          description: 'RBAC para control granular de acceso interno.',
        },
        {
          title: 'Alertas y Monitoreo',
          description: 'Notificaciones proactivas de anomalías y SLAs.',
        },
      ]}
      footer={commonFooter}
    />,

    // Slide 10: M5 - Calidad y Go-Live Final
    <MilestoneSlide
      key="m5-golive"
      title="M5: Calidad y Go-Live Final"
      subtitle="Semana 8 → v5 Live"
      price="$5.081.633 COP"
      priceSubtitle="Go-Live y aseguramiento de calidad"
      deliverables={[
        {
          title: 'Testing End-to-End',
          description: 'Pruebas completas de todos los scopes y flujos de autenticación.',
        },
        {
          title: 'Performance Testing',
          description: 'Validación de carga, latencia y capacidad del sistema.',
        },
        {
          title: 'Security Audit',
          description: 'Revisión de seguridad y cumplimiento de estándares.',
        },
        {
          title: 'Documentación Final',
          description: 'Guías de operación, runbooks y documentación técnica completa.',
        },
        {
          title: 'Capacitación',
          description: 'Transferencia de conocimiento al equipo de Deprisa.',
        },
        {
          title: 'Go-Live Production',
          description: 'Despliegue final a producción con monitoreo activo.',
        },
      ]}
      footer={commonFooter}
    />,

    // Slide 11: Tabla Resumen Inversión Total
    <TableSlide
      key="investment-summary"
      title="Resumen Inversión Total"
      subtitle="Desglose de costos por milestone — Inversión total del proyecto"
      headers={['Milestone', 'Descripción', 'Duración', 'Inversión COP']}
      rows={[
        {
          Milestone: 'M1',
          Descripción: 'APIM Gateway y Control',
          Duración: '2 semanas',
          'Inversión COP': '$11.081.633',
        },
        {
          Milestone: 'M2',
          Descripción: 'Arquitectura + Seguridad',
          Duración: '2 semanas',
          'Inversión COP': '$12.795.918',
        },
        {
          Milestone: 'M3',
          Descripción: 'Funcionalidades y B2B',
          Duración: '2 semanas',
          'Inversión COP': '$11.724.490',
        },
        {
          Milestone: 'M4',
          Descripción: 'Interno y Gobierno',
          Duración: '2 semanas',
          'Inversión COP': '$5.653.061',
        },
        {
          Milestone: 'M5',
          Descripción: 'Calidad y Go-Live Final',
          Duración: '1 semana',
          'Inversión COP': '$5.081.633',
        },
        {
          Milestone: '',
          Descripción: '',
          Duración: '',
          'Inversión COP': '',
        },
        {
          Milestone: 'TOTAL',
          Descripción: 'Inversión Total del Proyecto',
          Duración: '8 semanas',
          'Inversión COP': '$46.336.735',
        },
        {
          Milestone: 'PRECIO CON DESCUENTO',
          Descripción: 'Descuento 5,77%',
          Duración: '',
          'Inversión COP': '$43,663,462',
        },
      ]}
      paymentTermsBox={
        <div>
          <h4>Condiciones de Pago</h4>
          <p>
            Pago por milestone. Cada entrega se completa y verifica antes de proceder 
            con el pago correspondiente para iniciar el siguiente milestone.
          </p>
        </div>
      }
      footer={commonFooter}
    />,

    <ActionableSlide
      key="actionables"
      title="Accionables: On-boarding"
      subtitle="Project Kickoff"
      steps={[
        {
          title: 'Acuerdo & Llamada de Inicio',
          description: 'Finalizamos el Acuerdo, se aprueba el SOW, se realiza el primer pago y nos alineamos en una llamada de inicio para activar el proyecto.',
          highlighted: true,
        },
        {
          title: 'Configuración de Accesos',
          description: 'Solicitamos las credenciales, cuentas y herramientas necesarias para comenzar de forma eficiente.',
          highlighted: true,
        },
        {
          title: 'Planificación & Roadmap',
          description: 'Cerramos el plan del proyecto, definimos prioridades y alineamos expectativas con el equipo.',
          highlighted: true,
        },
        {
          title: 'Inicio de la Fase 1',
          description: 'Entramos oficialmente en la construcción e implementación del proyecto.',
          highlighted: false,
        },
      ]}
      footer={commonFooter}
    />,
  ];

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' && currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else if (e.key === 'Escape') {
      window.history.back();
    }
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown as any);
    return () => window.removeEventListener('keydown', handleKeyDown as any);
  });

  return (
    <div className="presentation-container">
      {slides[currentSlide]}
      <div
        className="top-controls-zone"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      <div
        className={`top-controls ${showControls ? 'visible' : ''}`}
        onMouseMove={handleMouseMove}
      >
        <button className="back-button" onClick={() => window.history.back()}>
          ← Volver al inicio
        </button>
        <div className="slide-navigation">
          <button
            onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
            disabled={currentSlide === 0}
          >
            ← Anterior
          </button>
          <span>
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
            disabled={currentSlide === slides.length - 1}
          >
            Siguiente →
          </button>
        </div>
      </div>
    </div>
  );
};

export default Presentation;

/*
 * SLIDES VERSIÓN LITE - COMENTADOS TEMPORALMENTE
 * Para restaurar estos slides, copia y pega el código debajo en el array de slides,
 * después del slide 11 (Resumen Inversión Total) y antes del slide de Accionables.
 *
 * Los slides incluyen:
 * 1. Presentación de la Versión Lite (TwoColumnSlide)
 * 2. Comparación Versión Completa vs Lite (TableSlide)
 * 3. ¿Por qué la versión lite tarda más? (TwoColumnSlide)
 */

/*
    // Slide 12: Presentación de la Versión Lite
    <TwoColumnSlide
      key="lite-version-intro"
      title="Alternativa: Versión Lite"
      leftColumn={{
        title: 'Alcance Reducido',
        subtitle: 'Funcionalidad esencial con menor inversión inicial',
        backgroundColor: '#f5f5f5',
        items: [
          { number: '01', text: 'Duración: 12-14 semanas (vs 8 semanas versión completa)' },
          { number: '02', text: 'Inversión inicial: $30.971.154 COP' },
          { number: '03', text: '3 milestones secuenciales' },
          { number: '04', text: 'Scopes: tracking.basic + tracking.events' },
          { number: '05', text: 'Equipo: 1-2 desarrolladores generalistas' },
        ],
      }}
      rightColumn={{
        title: 'Limitaciones Técnicas',
        subtitle: 'Compromisos arquitectónicos y deuda técnica',
        backgroundColor: '#ffffff',
        items: [
          { number: '01', text: 'Arquitectura monolítica (sin separación Front/API)' },
          { number: '02', text: 'Autenticación básica con API Keys (sin OAuth2)' },
          { number: '03', text: 'Sin capacidad B2B ni integración Call Center' },
          { number: '04', text: 'Desarrollo secuencial (sin paralelismo)' },
          { number: '05', text: 'Requiere refactorización completa posterior: +$20-30M COP' },
        ],
      }}
      footer={commonFooter}
    />,

    // Slide 13: Comparación Versión Completa vs Lite
    <TableSlide
      key="version-comparison"
      title="Comparación: Versión Completa vs Versión Lite"
      subtitle="Análisis de inversión, tiempo y deuda técnica"
      headers={['Aspecto', 'Versión Completa', 'Versión Lite']}
      rows={[
        {
          Aspecto: 'Duración',
          'Versión Completa': '8 semanas',
          'Versión Lite': '12-14 semanas (+75%)',
        },
        {
          Aspecto: 'Inversión Inicial',
          'Versión Completa': '$46.336.735 COP',
          'Versión Lite': '$30.971.154 COP',
        },
        {
          Aspecto: 'Arquitectura',
          'Versión Completa': 'Enterprise (Front/API, Hub & Spoke)',
          'Versión Lite': 'Monolítico',
        },
        {
          Aspecto: 'Seguridad',
          'Versión Completa': 'OAuth2 + Entra ID',
          'Versión Lite': 'API Keys',
        },
        {
          Aspecto: 'Deuda Técnica',
          'Versión Completa': 'Ninguna',
          'Versión Lite': 'Alta (refactorización +$20-30M)',
        },
        {
          Aspecto: 'Costo Real Total',
          'Versión Completa': '$46.336.735 COP',
          'Versión Lite': '$51-61M COP',
        },
      ]}
      paymentTermsBox={
        <div>
          <h4>¿Por qué recomendamos la Versión Completa?</h4>
          <p>
            La versión lite ahorra $15M hoy, pero requiere <strong>75% más tiempo</strong> y genera deuda técnica que
            costará <strong>$20-30M adicionales</strong> en refactorización. La versión completa le entrega
            <strong> arquitectura enterprise lista para escalar en 8 semanas</strong>.
          </p>
        </div>
      }
      footer={commonFooter}
    />,

    // Slide 14: ¿Por qué la versión lite tarda más?
    <TwoColumnSlide
      key="why-lite-takes-longer"
      title="¿Por qué la versión lite tarda más tiempo?"
      leftColumn={{
        title: 'Versión Completa',
        subtitle: 'Trabajo en paralelo y especialización',
        backgroundColor: '#e8f5e9',
        items: [
          { number: '01', text: '4-6 especialistas trabajando en paralelo en diferentes componentes' },
          { number: '02', text: 'Arquitectura correcta desde el inicio evita refactorización' },
          { number: '03', text: 'No hay curvas de aprendizaje — cada persona conoce su dominio' },
          { number: '04', text: 'Despliegues incrementales permiten validación temprana' },
          { number: '05', text: 'Resultado: 8 semanas con arquitectura enterprise lista para escalar' },
        ],
      }}
      rightColumn={{
        title: 'Versión Lite',
        subtitle: 'Trabajo secuencial y deuda técnica',
        backgroundColor: '#ffebee',
        items: [
          { number: '01', text: '1-2 generalistas trabajando secuencialmente (sin paralelismo)' },
          { number: '02', text: 'Arquitectura monolítica requiere refactorización completa después' },
          { number: '03', text: 'Curvas de aprendizaje en múltiples áreas reducen velocidad 60-70%' },
          { number: '04', text: 'Despliegues complejos por acoplamiento entre componentes' },
          { number: '05', text: 'Resultado: 12-14 semanas + $20-30M adicionales para refactorizar' },
        ],
      }}
      footer={commonFooter}
    />,
*/
